// src/components/templates/diary/DiaryMeetingEditor.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, Clock, MapPin, Users, List, 
  CheckSquare, MessageSquare, Play, CheckCircle, 
  Plus, Trash2, Wand2, ArrowLeft, Save, Repeat,
  ChevronDown, ChevronRight, Edit3, FileText, Target, X,
  Mic, StopCircle, Video, MonitorPlay, Lightbulb, Info, RefreshCw, StickyNote,
  Link as LinkIcon, Paperclip, ExternalLink, Download, Upload, Music, LayoutTemplate, Copy
} from 'lucide-react';
import { Button, Input, TextArea, Card, Badge, Select, MeetingStatus, MinuteType } from './UIComponents';
import { WorkspaceSelector } from './WorkspaceSelector';

export const DiaryMeetingEditor = ({ 
  meeting: initialMeeting, 
  onSave, 
  onBack, 
  templates = [], 
  onSaveTemplate,
  aiService,
  customStyling, 
  // Workspace props
  bookId,
  user,
  workspaces = [],
  onWorkspaceChange
}) => {
  const [meeting, setMeeting] = useState(initialMeeting);
  const [activeTab, setActiveTab] = useState('plan');
  const [newParticipant, setNewParticipant] = useState('');
  const [newObjective, setNewObjective] = useState('');
  const [isGeneratingAgenda, setIsGeneratingAgenda] = useState(false);
  
  // Template State
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  // Minutes State
  const [minuteInput, setMinuteInput] = useState('');
  const [minuteType, setMinuteType] = useState('NOTE');
  const [taskOwner, setTaskOwner] = useState('');
  const [taskDeadline, setTaskDeadline] = useState('');
  const [isRefining, setIsRefining] = useState(false);

  // Task Audio State
  const [taskAudioMode, setTaskAudioMode] = useState('NONE');
  const [isTaskRecording, setIsTaskRecording] = useState(false);
  const [taskRecordingDuration, setTaskRecordingDuration] = useState(0);
  const [taskAudioData, setTaskAudioData] = useState(null);
  
  const taskMediaRecorderRef = useRef(null);
  const taskAudioChunksRef = useRef([]);
  const taskTimerRef = useRef(null);
  const taskFileInputRef = useRef(null);

  // Notes Tab State
  const [noteContent, setNoteContent] = useState('');
  const [noteCategory, setNoteCategory] = useState('GENERAL');

  // Meeting Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Agenda Item Details State
  const [expandedAgendaId, setExpandedAgendaId] = useState(null);
  const [expandedPlanAgendaId, setExpandedPlanAgendaId] = useState(null);

  // Resource Input State
  const [newResourceLink, setNewResourceLink] = useState('');
  const [newResourceTitle, setNewResourceTitle] = useState('');
  const fileInputRef = useRef(null);

  // Summary State
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // Auto-switch tab based on status
  useEffect(() => {
    if (meeting.status === MeetingStatus.ACTIVE) setActiveTab('run');
    if (meeting.status === MeetingStatus.COMPLETED) setActiveTab('review');
  }, [meeting.status]);

  const handleUpdate = (field, value) => {
    setMeeting(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(meeting);
  };

  // --- TEMPLATE LOGIC ---
  const handleSaveAsTemplate = () => {
    if (!newTemplateName.trim() || !onSaveTemplate) return;
    const template = {
      id: Math.random().toString(36).substr(2, 9),
      name: newTemplateName,
      defaultTitle: meeting.title,
      agenda: (meeting.agenda || []).map(a => ({...a, completed: false, notes: '', decisions: ''})),
      participants: (meeting.participants || []).map(p => ({...p, present: false})),
      objectives: meeting.objectives || []
    };
    onSaveTemplate(template);
    setShowTemplateModal(false);
    setNewTemplateName('');
  };

  const handleApplyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    if ((meeting.agenda || []).length > 0 && !window.confirm("This will overwrite your current agenda and objectives. Continue?")) {
      return;
    }

    const newAgenda = (template.agenda || []).map(item => ({
      ...item,
      id: Math.random().toString(36).substr(2, 9),
      completed: false,
      notes: '',
      decisions: ''
    }));

    const newParticipants = [
      ...(meeting.participants || []), 
      ...(template.participants || []).filter(tp => !(meeting.participants || []).some(mp => mp.email === tp.email)).map(p => ({
         ...p,
         id: Math.random().toString(36).substr(2, 9),
         present: false
      }))
    ];

    setMeeting(prev => ({
      ...prev,
      title: prev.title === 'New Meeting' ? (template.defaultTitle || prev.title) : prev.title,
      agenda: newAgenda,
      participants: newParticipants,
      objectives: template.objectives ? [...template.objectives] : []
    }));
  };

  // --- PDF EXPORT LOGIC ---
  const exportAgendaToPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      
      const doc = new jsPDF();
      
      doc.setFontSize(22);
      doc.setTextColor(33, 33, 33);
      doc.text(meeting.title || "Meeting Agenda", 14, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(100);
      const dateStr = `Date: ${meeting.date}   |   Time: ${meeting.time} (${meeting.durationMinutes} min)`;
      const locStr = `Location: ${meeting.location || 'N/A'}   |   Type: ${meeting.type === 'IN_PERSON' ? 'In Person' : 'Virtual'}`;
      
      doc.text(dateStr, 14, 30);
      doc.text(locStr, 14, 36);
      
      doc.setDrawColor(200, 200, 200);
      doc.line(14, 42, 196, 42);

      let startY = 50;

      if (meeting.objectives && meeting.objectives.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(79, 70, 229);
        doc.text("Meeting Objectives", 14, startY);
        
        doc.setFontSize(11);
        doc.setTextColor(50);
        startY += 8;
        meeting.objectives.forEach((obj) => {
          doc.text(`• ${obj}`, 18, startY);
          startY += 6;
        });
        startY += 6;
      }

      const tableColumn = ["Time / Duration", "Item", "Presenter", "Resources"];
      const tableRows = (meeting.agenda || []).map(item => {
        const resourceText = (item.resources || []).map(r => r.title).join(', ') || '-';
        return [
          `${item.durationMinutes} min`,
          item.title,
          item.presenter || '-',
          resourceText
        ];
      });

      autoTable(doc, {
        startY: startY,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        headStyles: { 
          fillColor: [79, 70, 229],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 10,
          cellPadding: 4,
          overflow: 'linebreak'
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 40 },
          3: { cellWidth: 50 }
        }
      });

      doc.save(`${(meeting.title || 'meeting').replace(/\s+/g, '_')}_Agenda.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF export failed. Please ensure jspdf is installed.');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // --- TASK AUDIO LOGIC ---
  const startTaskRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      taskMediaRecorderRef.current = mediaRecorder;
      taskAudioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) taskAudioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
         const audioBlob = new Blob(taskAudioChunksRef.current, { type: 'audio/webm' });
         const reader = new FileReader();
         reader.readAsDataURL(audioBlob);
         reader.onloadend = () => {
             setTaskAudioData({
                 url: reader.result,
                 type: 'RECORDING',
                 duration: formatTime(taskRecordingDuration)
             });
         };
         stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsTaskRecording(true);
      setTaskRecordingDuration(0);
      taskTimerRef.current = window.setInterval(() => {
        setTaskRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
       console.error("Task mic error:", err);
       alert("Microphone access denied for task note.");
    }
  };

  const stopTaskRecording = () => {
     if (taskMediaRecorderRef.current && isTaskRecording) {
       taskMediaRecorderRef.current.stop();
       setIsTaskRecording(false);
       if (taskTimerRef.current) {
          clearInterval(taskTimerRef.current);
          taskTimerRef.current = null;
       }
     }
  };

  const handleTaskFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
            if(ev.target?.result) {
                setTaskAudioData({
                    url: ev.target.result,
                    type: 'UPLOAD',
                    duration: 'File'
                });
            }
        };
        reader.readAsDataURL(file);
    }
  };

  const clearTaskAudio = () => {
    setTaskAudioData(null);
    setTaskAudioMode('NONE');
    setTaskRecordingDuration(0);
  };

  // --- MEETING RECORDING LOGIC ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      timerRef.current = window.setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error("Error accessing microphone:", err);
      alert("Could not access microphone. Please allow permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleTranscribeRecording = async () => {
    if (audioChunksRef.current.length === 0) return;
    
    setIsTranscribing(true);
    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    
    const reader = new FileReader();
    reader.readAsDataURL(audioBlob);
    reader.onloadend = async () => {
      try {
        if (aiService?.transcribeAudio) {
          const base64Audio = reader.result.split(',')[1];
          const transcript = await aiService.transcribeAudio(base64Audio, 'audio/webm');
          setMinuteInput(prev => (prev ? prev + '\n\n' : '') + `[Transcription]: ${transcript}`);
        } else {
          alert("AI transcription service not available.");
        }
        setRecordingDuration(0);
        audioChunksRef.current = [];
      } catch (error) {
        console.error(error);
        alert("Transcription failed. Please try again.");
      } finally {
        setIsTranscribing(false);
      }
    };
  };

  // --- OBJECTIVES LOGIC ---
  const addObjective = () => {
    if (!newObjective.trim()) return;
    const currentObjectives = meeting.objectives || [];
    handleUpdate('objectives', [...currentObjectives, newObjective]);
    setNewObjective('');
  };

  const removeObjective = (index) => {
    const currentObjectives = meeting.objectives || [];
    handleUpdate('objectives', currentObjectives.filter((_, i) => i !== index));
  };

  // --- NOTES LOGIC ---
  const addNote = () => {
    if (!noteContent.trim()) return;
    const newNote = {
      id: Math.random().toString(36).substr(2, 9),
      category: noteCategory,
      content: noteContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    handleUpdate('meetingNotes', [...(meeting.meetingNotes || []), newNote]);
    setNoteContent('');
  };

  const deleteNote = (id) => {
    handleUpdate('meetingNotes', (meeting.meetingNotes || []).filter(n => n.id !== id));
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'IDEA': return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      case 'UPDATE': return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'TASK_UPDATE': return 'bg-green-50 text-green-800 border-green-200';
      default: return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'IDEA': return <Lightbulb className="w-4 h-4" />;
      case 'UPDATE': return <Info className="w-4 h-4" />;
      case 'TASK_UPDATE': return <RefreshCw className="w-4 h-4" />;
      default: return <StickyNote className="w-4 h-4" />;
    }
  };

  // --- AGENDA LOGIC ---
  const addAgendaItem = () => {
    const newItem = {
      id: Math.random().toString(36).substr(2, 9),
      title: 'New Agenda Item',
      durationMinutes: 15,
      presenter: '',
      completed: false,
      notes: '',
      decisions: '',
      resources: []
    };
    handleUpdate('agenda', [...(meeting.agenda || []), newItem]);
  };

  const updateAgendaItem = (id, field, value) => {
    const newAgenda = (meeting.agenda || []).map(item => 
      item.id === id ? { ...item, [field]: value } : item
    );
    handleUpdate('agenda', newAgenda);
  };

  const deleteAgendaItem = (id) => {
    handleUpdate('agenda', (meeting.agenda || []).filter(item => item.id !== id));
  };

  const handleGenerateAgenda = async () => {
    if (!aiService?.generateAgendaSuggestion) {
      alert("AI service not available");
      return;
    }
    setIsGeneratingAgenda(true);
    try {
      const suggestions = await aiService.generateAgendaSuggestion(meeting.title, meeting.durationMinutes);
      handleUpdate('agenda', suggestions);
    } catch (e) {
      alert("Failed to generate agenda. Please check API key or try again.");
    } finally {
      setIsGeneratingAgenda(false);
    }
  };

  const toggleAgendaExpand = (id) => {
    setExpandedAgendaId(prev => prev === id ? null : id);
  };
  
  const togglePlanAgendaExpand = (id) => {
    setExpandedPlanAgendaId(prev => prev === id ? null : id);
    setNewResourceLink('');
    setNewResourceTitle('');
  };

  // --- RESOURCE LOGIC ---
  const addResource = (agendaId, type, title, url) => {
    const item = (meeting.agenda || []).find(a => a.id === agendaId);
    if (!item) return;

    const newResource = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      title: title || (type === 'FILE' ? 'Untitled File' : 'Untitled Link'),
      url
    };

    updateAgendaItem(agendaId, 'resources', [...(item.resources || []), newResource]);
  };

  const handleAddLink = (agendaId) => {
    if (!newResourceLink) return;
    addResource(agendaId, 'LINK', newResourceTitle || newResourceLink, newResourceLink);
    setNewResourceLink('');
    setNewResourceTitle('');
  };

  const handleFileSelect = (agendaId, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      addResource(agendaId, 'FILE', file.name, url);
    }
  };

  const deleteResource = (agendaId, resourceId) => {
    const item = (meeting.agenda || []).find(a => a.id === agendaId);
    if (!item || !item.resources) return;
    updateAgendaItem(agendaId, 'resources', item.resources.filter(r => r.id !== resourceId));
  };

  // --- PARTICIPANT LOGIC ---
  const addParticipant = () => {
    if (!newParticipant.trim()) return;
    const p = {
      id: Math.random().toString(36).substr(2, 9),
      name: newParticipant,
      email: '',
      role: 'Participant',
      present: false
    };
    handleUpdate('participants', [...(meeting.participants || []), p]);
    setNewParticipant('');
  };

  const toggleAttendance = (id) => {
    const newParticipants = (meeting.participants || []).map(p => 
      p.id === id ? { ...p, present: !p.present } : p
    );
    handleUpdate('participants', newParticipants);
  };

  // --- MINUTES LOGIC ---
  const addMinuteEntry = () => {
    if (!minuteInput.trim()) return;

    const entry = {
      id: Math.random().toString(36).substr(2, 9),
      type: minuteType,
      content: minuteInput,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      assigneeId: minuteType === MinuteType.TASK ? taskOwner : undefined,
      dueDate: minuteType === MinuteType.TASK ? taskDeadline : undefined,
      completed: false,
      audio: (minuteType === MinuteType.TASK && taskAudioData) ? taskAudioData : undefined
    };

    handleUpdate('minutes', [...(meeting.minutes || []), entry]);
    setMinuteInput('');
    
    if (minuteType === MinuteType.TASK) {
      setTaskOwner('');
      setTaskDeadline('');
      clearTaskAudio();
    }
    setMinuteType('NOTE');
  };

  const toggleTaskCompletion = (id) => {
    const newMinutes = (meeting.minutes || []).map(m => 
      m.id === id ? { ...m, completed: !m.completed } : m
    );
    handleUpdate('minutes', newMinutes);
  };

  const handleRefineNote = async () => {
    if (!minuteInput.trim() || !aiService?.refineMinuteNote) return;
    setIsRefining(true);
    try {
      const refined = await aiService.refineMinuteNote(minuteInput);
      setMinuteInput(refined);
    } finally {
      setIsRefining(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!aiService?.generateMeetingSummary) {
      alert("AI service not available");
      return;
    }
    setIsGeneratingSummary(true);
    try {
      const summary = await aiService.generateMeetingSummary(meeting.title, meeting.agenda, meeting.minutes);
      handleUpdate('summary', summary);
    } catch (e) {
      console.error(e);
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const startMeeting = () => {
    handleUpdate('status', MeetingStatus.ACTIVE);
    setActiveTab('run');
  };

  const endMeeting = () => {
    handleUpdate('status', MeetingStatus.COMPLETED);
    stopRecording();
    setActiveTab('review');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden relative">
      
      {/* Template Modal */}
      {showTemplateModal && (
        <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
           <Card className="p-6 w-full max-w-md bg-white">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Save as Template</h3>
              <p className="text-sm text-gray-500 mb-4">
                Save the current agenda, objectives, and participants as a reusable template.
              </p>
              <Input 
                label="Template Name"
                placeholder="e.g., Monthly Board Meeting"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
              />
              <div className="flex justify-end gap-3 mt-6">
                 <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
                 <Button onClick={handleSaveAsTemplate} disabled={!newTemplateName.trim()}>Save Template</Button>
              </div>
           </Card>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <div>
            <h1 className="text-xl font-bold text-gray-900">{meeting.title || 'Untitled Meeting'}</h1>          
            <div className="flex items-center text-sm text-gray-500 mt-1 space-x-4">
              <span className="flex items-center"><Calendar className="w-4 h-4 mr-1"/> {meeting.date}</span>
              <span className="flex items-center"><Clock className="w-4 h-4 mr-1"/> {meeting.time} ({meeting.durationMinutes} min)</span>
              <Badge color={
                meeting.status === MeetingStatus.PLANNED ? 'blue' :
                meeting.status === MeetingStatus.ACTIVE ? 'green' : 'gray'
              }>{meeting.status}</Badge>
              {meeting.recurrence && meeting.recurrence !== 'NONE' && (
                <Badge color="purple"><Repeat className="w-3 h-3 mr-1" /> {meeting.recurrence}</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {onSaveTemplate && (
            <Button onClick={() => setShowTemplateModal(true)} variant="secondary" size="sm">
               <LayoutTemplate className="w-4 h-4 mr-2" /> Save as Template
            </Button>
          )}

          {meeting.status === MeetingStatus.PLANNED && (
             <Button onClick={startMeeting} className="bg-green-600 hover:bg-green-700"
              style={{ backgroundColor: customStyling.primaryColor }}
             >
               <Play className="w-4 h-4 mr-2" /> Start Meeting
             </Button>
          )}
          {meeting.status === MeetingStatus.ACTIVE && (
             <Button onClick={endMeeting} variant="danger">
               <CheckCircle className="w-4 h-4 mr-2" /> End Meeting
             </Button>
          )}
          <Button onClick={handleSave} variant="secondary">
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b px-6 flex space-x-6 text-sm font-medium shrink-0">
        <button onClick={() => setActiveTab('plan')} style={{ color: activeTab === 'plan' ? customStyling.primaryColor : undefined }}
         className={`py-3 border-b-2 px-2 transition-colors ${activeTab === 'plan' ? 'border-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Plan (Details & Agenda)
        </button>
        <button onClick={() => setActiveTab('run')} style={{ color: activeTab === 'run' ? customStyling.primaryColor : undefined }}
        className={`py-3 border-b-2 px-2 transition-colors ${activeTab === 'run' ? 'border-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Run (Attendance & Minutes)
        </button>
        <button onClick={() => setActiveTab('notes')} style={{ color: activeTab === 'notes' ? customStyling.primaryColor : undefined }}
        className={`py-3 border-b-2 px-2 transition-colors ${activeTab === 'notes' ? 'border-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Notes (Ideas & Updates)
        </button>
        <button onClick={() => setActiveTab('review')} style={{ color: activeTab === 'review' ? customStyling.primaryColor : undefined }}
        className={`py-3 border-b-2 px-2 transition-colors ${activeTab === 'review' ? 'border-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Review (Summary & Tasks)
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          
          {/* --- PLAN TAB --- */}
          {activeTab === 'plan' && (
            <>
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  {/* Workspace Selector */}
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1 cursor-pointer py-1 border border-gray-200 rounded-md hover:bg-gray-50">
                      {bookId && user && (
                        <WorkspaceSelector
                          bookId={bookId}
                          user={user}
                          currentWorkspaceId={meeting.workspaceId}
                          onWorkspaceChange={(ws) => {
                            handleUpdate('workspaceId', ws.id);
                            onWorkspaceChange?.(ws);
                          }}
                          compact
                        />
                        
                      )}
                    <ChevronDown className="w-4 h-4 text-gray-400" /> 
                  </div>

                  <h3 className="text-lg font-semibold text-gray-800">Meeting Details</h3>
                  </div>
                  {templates.length > 0 && (
                     <select className="text-sm p-2 bg-gray-50 border border-gray-200 rounded-md outline-none focus:border-indigo-500 w-48" onChange={(e) => { if(e.target.value) handleApplyTemplate(e.target.value); e.target.value = ''; }}>
                        <option value="">Load Template...</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                     </select>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Title" value={meeting.title} onChange={(e) => handleUpdate('title', e.target.value)} />
                  
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Select label="Type" value={meeting.type || 'VIRTUAL'} onChange={(e) => handleUpdate('type', e.target.value)} options={[{ label: 'Virtual / Online', value: 'VIRTUAL' },{ label: 'In Person', value: 'IN_PERSON' }]} />
                    </div>
                    <div className="flex-1">
                      <Input label={meeting.type === 'IN_PERSON' ? 'Room / Location' : 'Platform'} value={meeting.location || ''} onChange={(e) => handleUpdate('location', e.target.value)} placeholder={meeting.type === 'IN_PERSON' ? 'e.g. Conf Room A' : 'e.g. Zoom, Google Meet'} />
                    </div>
                  </div>

                  {meeting.type === 'VIRTUAL' && (
                    <div className="col-span-1 md:col-span-2">
                       <Input label="Meeting Link" value={meeting.meetingLink || ''} onChange={(e) => handleUpdate('meetingLink', e.target.value)} placeholder="https://zoom.us/j/..." />
                    </div>
                  )}
                  
                  <div className="flex gap-4">
                     <div className="flex-1"><Input label="Date" type="date" value={meeting.date} onChange={(e) => handleUpdate('date', e.target.value)} /></div>
                     <div className="flex-1">
                       <Select label="Recurrence" value={meeting.recurrence || 'NONE'} onChange={(e) => handleUpdate('recurrence', e.target.value)} options={[{ label: 'Does not repeat', value: 'NONE' },{ label: 'Daily', value: 'DAILY' },{ label: 'Weekly', value: 'WEEKLY' },{ label: 'Monthly', value: 'MONTHLY' }]} />
                     </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1"><Input label="Time" type="time" value={meeting.time} onChange={(e) => handleUpdate('time', e.target.value)} /></div>
                    <div className="flex-1"><Input label="Duration (min)" type="number" value={meeting.durationMinutes} onChange={(e) => handleUpdate('durationMinutes', parseInt(e.target.value))} /></div>
                  </div>
                </div>

                {/* Objectives Section */}
                <div className="mt-6 border-t border-gray-100 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Objectives</label>
                  <div className="flex space-x-2 mb-3">
                    <Input placeholder="Add a key objective..." value={newObjective} onChange={(e) => setNewObjective(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addObjective()} />
                    <Button onClick={addObjective} variant="secondary" className="px-3"><Plus className="w-5 h-5" /></Button>
                  </div>
                  <div className="space-y-2">
                    {(meeting.objectives || []).map((obj, index) => (
                      <div key={index} className="flex items-center justify-between bg-indigo-50 p-2.5 rounded-md border border-indigo-100 group">
                        <span className="text-gray-700 text-sm flex items-start"><Target className="w-4 h-4 mr-2.5 mt-0.5 text-indigo-500 shrink-0" />{obj}</span>
                        <button onClick={() => removeObjective(index)} className="text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    {(!meeting.objectives || meeting.objectives.length === 0) && <p className="text-xs text-gray-400 italic flex items-center"><Target className="w-3 h-3 mr-1.5" /> No objectives defined yet.</p>}
                  </div>
                </div>
              </Card>

              {/* Agenda Card */}
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Agenda</h3>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="secondary" onClick={exportAgendaToPDF} title="Export Agenda to PDF"><Download className="w-4 h-4 mr-2" /> Export PDF</Button>
                    {aiService && <Button size="sm" variant="secondary" onClick={handleGenerateAgenda} isLoading={isGeneratingAgenda}><Wand2 className="w-4 h-4 mr-2 text-indigo-500" /> AI Suggest</Button>}
                    <Button size="sm" onClick={addAgendaItem}><Plus className="w-4 h-4 mr-2" /> Add Item</Button>
                  </div>
                </div>
                
                {(meeting.agenda || []).length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">No agenda items yet. Generate with AI, load a template, or add manually.</div>
                ) : (
                  <div className="space-y-3">
                    {(meeting.agenda || []).map((item) => (
                      <div key={item.id} className="bg-gray-50 rounded-md border border-gray-200">
                         <div className="flex items-center space-x-3 p-3">
                           <button onClick={() => togglePlanAgendaExpand(item.id)} className="text-gray-400 hover:text-gray-600 transition-colors">
                              {expandedPlanAgendaId === item.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                           </button>
                           <div className="flex-1"><input className="bg-transparent font-medium text-gray-800 w-full outline-none" value={item.title} onChange={(e) => updateAgendaItem(item.id, 'title', e.target.value)} placeholder="Item title" /></div>
                           <div className="w-24"><input type="number" className="bg-transparent text-sm text-gray-600 w-full outline-none text-right" value={item.durationMinutes} onChange={(e) => updateAgendaItem(item.id, 'durationMinutes', parseInt(e.target.value))} /></div>
                           <span className="text-sm text-gray-400">min</span>
                           <div className="w-32"><input className="bg-transparent text-sm text-gray-600 w-full outline-none border-b border-gray-200 focus:border-indigo-500" value={item.presenter || ''} onChange={(e) => updateAgendaItem(item.id, 'presenter', e.target.value)} placeholder="Presenter" /></div>
                           <button onClick={() => deleteAgendaItem(item.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                         </div>

                         {expandedPlanAgendaId === item.id && (
                           <div className="p-3 bg-white border-t border-gray-100 rounded-b-md">
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center"><Paperclip className="w-3 h-3 mr-1.5" /> Pre-read Materials & Resources</h4>
                              <div className="space-y-2 mb-4">
                                {(!item.resources || item.resources.length === 0) && <p className="text-xs text-gray-400 italic">No resources attached.</p>}
                                {(item.resources || []).map(resource => (
                                  <div key={resource.id} className="flex items-center justify-between p-2 bg-indigo-50 rounded border border-indigo-100 group">
                                     <div className="flex items-center overflow-hidden">
                                       {resource.type === 'LINK' ? <LinkIcon className="w-3 h-3 mr-2 text-indigo-500 shrink-0" /> : <FileText className="w-3 h-3 mr-2 text-indigo-500 shrink-0" />}
                                       <a href={resource.url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-700 hover:underline truncate">{resource.title}</a>
                                     </div>
                                     <button onClick={() => deleteResource(item.id, resource.id)} className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                                  </div>
                                ))}
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 items-end sm:items-center bg-gray-50 p-2 rounded">
                                 <div className="flex-1 w-full space-y-2 sm:space-y-0 sm:flex sm:gap-2">
                                   <input placeholder="Resource Title (e.g. Q3 Strategy)" className="text-xs p-2 rounded border border-gray-300 w-full outline-none focus:border-indigo-500" value={newResourceTitle} onChange={(e) => setNewResourceTitle(e.target.value)} />
                                   <input placeholder="https://..." className="text-xs p-2 rounded border border-gray-300 w-full outline-none focus:border-indigo-500" value={newResourceLink} onChange={(e) => setNewResourceLink(e.target.value)} />
                                 </div>
                                 <div className="flex gap-1 shrink-0">
                                   <Button size="sm" variant="secondary" onClick={() => handleAddLink(item.id)} disabled={!newResourceLink} className="text-xs py-1.5 h-[34px]">Add Link</Button>
                                   <div className="relative">
                                     <input type="file" className="hidden" ref={fileInputRef} onChange={(e) => handleFileSelect(item.id, e)} />
                                     <Button size="sm" variant="secondary" className="text-xs py-1.5 h-[34px]" onClick={() => fileInputRef.current?.click()}>Upload File</Button>
                                   </div>
                                 </div>
                              </div>
                           </div>
                         )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Participants Card */}
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-gray-800">Participants</h3></div>
                <div className="flex space-x-2 mb-4">
                  <Input placeholder="Enter name" value={newParticipant} onChange={(e) => setNewParticipant(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addParticipant()} />
                  <Button onClick={addParticipant}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(meeting.participants || []).map(p => (
                    <div key={p.id} className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm border border-indigo-100">
                      <Users className="w-3 h-3 mr-2" />{p.name}
                      <button onClick={() => handleUpdate('participants', (meeting.participants || []).filter(mp => mp.id !== p.id))} className="ml-2 text-indigo-400 hover:text-indigo-900">×</button>
                    </div>
                  ))}
                  {(meeting.participants || []).length === 0 && <span className="text-gray-400 text-sm italic">No participants added.</span>}
                </div>
              </Card>
            </>
          )}

          {/* --- RUN TAB --- */}
          {activeTab === 'run' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-6">
                {meeting.type === 'IN_PERSON' && (
                   <Card className="p-4 border-l-4 border-l-red-500">
                     <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center"><Mic className="w-4 h-4 mr-2 text-red-500" />Meeting Audio</h3>
                     {isRecording ? (
                       <div className="flex flex-col items-center py-4 bg-red-50 rounded-lg border border-red-100">
                         <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse mb-2" />
                         <span className="text-2xl font-mono text-red-700 font-bold mb-3">{formatTime(recordingDuration)}</span>
                         <Button onClick={stopRecording} variant="danger" size="sm"><StopCircle className="w-4 h-4 mr-2" /> Stop Recording</Button>
                       </div>
                     ) : (
                       <div className="space-y-3">
                         <div className="flex gap-2"><Button onClick={startRecording} className="flex-1 bg-gray-800 hover:bg-black" size="sm"><Mic className="w-4 h-4 mr-2" /> Start Recording</Button></div>
                         {(audioChunksRef.current.length > 0 || recordingDuration > 0) && (
                            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 flex justify-between items-center">
                               <span>Recorded: {formatTime(recordingDuration)}</span>
                               <Button size="sm" variant="secondary" onClick={handleTranscribeRecording} isLoading={isTranscribing} disabled={isTranscribing}><Wand2 className="w-3 h-3 mr-1 text-purple-600" /> Transcribe</Button>
                            </div>
                         )}
                       </div>
                     )}
                   </Card>
                )}

                <Card className="p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Agenda Status</h3>
                  <div className="space-y-3">
                    {(meeting.agenda || []).map(item => (
                      <div key={item.id} className="border border-gray-100 rounded-lg overflow-hidden transition-all bg-white hover:border-indigo-100">
                        <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50" onClick={() => toggleAgendaExpand(item.id)}>
                          <div className="flex items-center flex-1 min-w-0 pr-2">
                             <input type="checkbox" checked={item.completed} onClick={(e) => e.stopPropagation()} onChange={(e) => updateAgendaItem(item.id, 'completed', e.target.checked)} className="mr-3 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 shrink-0" />
                            <div className="truncate">
                              <p className={`text-sm font-medium truncate ${item.completed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{item.title}</p>
                              <p className="text-xs text-gray-500">{item.durationMinutes} min • {item.presenter}</p>
                            </div>
                          </div>
                          {expandedAgendaId === item.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        </div>
                        
                        {expandedAgendaId === item.id && (
                          <div className="p-3 bg-gray-50 border-t border-gray-100 space-y-3">
                             {item.resources && item.resources.length > 0 && (
                               <div className="mb-3 pb-3 border-b border-gray-200/60">
                                 <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Resources</h5>
                                 <div className="space-y-1">
                                    {item.resources.map(res => (
                                      <a key={res.id} href={res.url} target="_blank" rel="noreferrer" className="flex items-center text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-1.5 rounded transition-colors">
                                        {res.type === 'LINK' ? <ExternalLink className="w-3 h-3 mr-2" /> : <FileText className="w-3 h-3 mr-2" />}
                                        <span className="truncate">{res.title}</span>
                                      </a>
                                    ))}
                                 </div>
                               </div>
                             )}
                             <div>
                               <label className="text-xs font-semibold text-gray-500 block mb-1">Notes</label>
                               <textarea className="w-full text-xs p-2 rounded border border-gray-200 focus:border-indigo-500 outline-none resize-none" rows={3} placeholder="Discussion points..." value={item.notes || ''} onChange={(e) => updateAgendaItem(item.id, 'notes', e.target.value)} />
                             </div>
                             <div>
                               <label className="text-xs font-semibold text-gray-500 block mb-1 text-blue-600">Decision Made</label>
                               <textarea className="w-full text-xs p-2 rounded border border-blue-100 bg-blue-50 focus:border-blue-300 outline-none resize-none text-blue-900" rows={2} placeholder="Decision reached..." value={item.decisions || ''} onChange={(e) => updateAgendaItem(item.id, 'decisions', e.target.value)} />
                             </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                   <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Attendance</h3>
                   <div className="space-y-2">
                     {(meeting.participants || []).map(p => (
                       <div key={p.id} className={`flex items-center justify-between p-2 rounded cursor-pointer border ${p.present ? 'bg-green-50 border-green-200' : 'bg-white border-transparent hover:bg-gray-50'}`} onClick={() => toggleAttendance(p.id)}>
                         <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-3 ${p.present ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <span className={`text-sm ${p.present ? 'font-medium text-green-900' : 'text-gray-600'}`}>{p.name}</span>
                         </div>
                         {p.present && <CheckCircle className="w-4 h-4 text-green-600" />}
                       </div>
                     ))}
                   </div>
                </Card>
              </div>

              <div className="lg:col-span-2 space-y-6">
                 <Card className="p-6 border-indigo-100 shadow-md">
                   <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><MessageSquare className="w-5 h-5 mr-2 text-indigo-600" /> Capture Minutes (Global)</h3>
                   <div className="space-y-3">
                     <TextArea rows={3} placeholder={meeting.type === 'IN_PERSON' ? "Type notes here or use the recorder on the left to transcribe audio..." : "Type your notes here..."} value={minuteInput} onChange={(e) => setMinuteInput(e.target.value)} className="resize-none text-base" />
                     
                     <div className="flex flex-wrap items-center gap-2 pb-2">
                       <span className="text-sm text-gray-500 mr-2">Type:</span>
                       <button onClick={() => setMinuteType('NOTE')} className={`px-3 py-1 rounded-full text-xs font-medium border ${minuteType === 'NOTE' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200'}`}>Note</button>
                       <button onClick={() => setMinuteType('DECISION')} className={`px-3 py-1 rounded-full text-xs font-medium border ${minuteType === 'DECISION' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>Decision</button>
                       <button onClick={() => setMinuteType('TASK')} className={`px-3 py-1 rounded-full text-xs font-medium border ${minuteType === 'TASK' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200'}`}>Action Item</button>
                     </div>

                     {minuteType === 'TASK' && (
                       <div className="flex flex-col gap-3 p-3 bg-amber-50 rounded-md border border-amber-100">
                         <div className="flex gap-2">
                           <select className="text-sm p-2 rounded border border-amber-200 outline-none w-1/2 bg-white" value={taskOwner} onChange={(e) => setTaskOwner(e.target.value)}>
                             <option value="">Select Owner</option>
                             {(meeting.participants || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                           </select>
                           <input type="date" className="text-sm p-2 rounded border border-amber-200 outline-none w-1/2 bg-white" value={taskDeadline} onChange={(e) => setTaskDeadline(e.target.value)} />
                         </div>

                         <div className="border-t border-amber-200/50 pt-2">
                            <label className="text-xs font-semibold text-amber-800 mb-2 block">Voice Note / Audio Attachment (Optional)</label>
                            {!taskAudioData && taskAudioMode === 'NONE' && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="secondary" onClick={() => setTaskAudioMode('RECORD')} className="text-xs bg-white h-8 border-amber-200 text-amber-800 hover:bg-amber-100"><Mic className="w-3 h-3 mr-1" /> Record</Button>
                                <Button size="sm" variant="secondary" onClick={() => setTaskAudioMode('UPLOAD')} className="text-xs bg-white h-8 border-amber-200 text-amber-800 hover:bg-amber-100"><Upload className="w-3 h-3 mr-1" /> Upload</Button>
                              </div>
                            )}
                            {taskAudioMode === 'RECORD' && !taskAudioData && (
                              <div className="flex items-center gap-2 bg-white p-2 rounded border border-amber-200">
                                 {isTaskRecording ? <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" /> : <div className="w-2 h-2 bg-gray-400 rounded-full" />}
                                 <span className="text-xs font-mono font-bold w-12">{formatTime(taskRecordingDuration)}</span>
                                 {!isTaskRecording ? <Button size="sm" onClick={startTaskRecording} className="h-6 text-xs px-2 bg-red-600 hover:bg-red-700">Start</Button> : <Button size="sm" onClick={stopTaskRecording} variant="secondary" className="h-6 text-xs px-2">Stop</Button>}
                                 <button onClick={clearTaskAudio} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-3 h-3"/></button>
                              </div>
                            )}
                            {taskAudioMode === 'UPLOAD' && !taskAudioData && (
                               <div className="flex items-center gap-2 bg-white p-2 rounded border border-amber-200">
                                  <input type="file" accept="audio/*" className="hidden" ref={taskFileInputRef} onChange={handleTaskFileUpload} />
                                  <Button size="sm" variant="secondary" onClick={() => taskFileInputRef.current?.click()} className="h-6 text-xs px-2">Select File</Button>
                                  <button onClick={clearTaskAudio} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-3 h-3"/></button>
                               </div>
                            )}
                            {taskAudioData && (
                               <div className="flex items-center gap-2 bg-green-50 p-2 rounded border border-green-200 text-green-800">
                                  {taskAudioData.type === 'RECORDING' ? <Mic className="w-3 h-3" /> : <Music className="w-3 h-3" />}
                                  <span className="text-xs font-medium">Audio Attached</span>
                                  <button onClick={clearTaskAudio} className="ml-auto text-green-600 hover:text-green-800"><Trash2 className="w-3 h-3"/></button>
                               </div>
                            )}
                         </div>
                       </div>
                     )}

                     <div className="flex justify-between items-center pt-2">
                        {aiService && <Button variant="ghost" size="sm" onClick={handleRefineNote} disabled={!minuteInput || isRefining} isLoading={isRefining} title="Use AI to polish the text"><Wand2 className="w-4 h-4 mr-2 text-purple-600" /> Refine Text</Button>}
                        {!aiService && <div />}
                        <Button onClick={addMinuteEntry} disabled={!minuteInput.trim()}>Add Entry</Button>
                     </div>
                   </div>
                 </Card>

                 <div className="space-y-4">
                   <h4 className="text-sm font-medium text-gray-500 uppercase">Live Feed</h4>
                   {(meeting.minutes || []).length === 0 && <p className="text-gray-400 text-sm">No entries yet.</p>}
                   {[...(meeting.minutes || [])].reverse().map(entry => (
                     <div key={entry.id} className="flex space-x-3 group">
                       <div className="flex flex-col items-center">
                         <div className="h-full w-px bg-gray-200 mb-1"></div>
                         <div className={`w-2 h-2 rounded-full shrink-0 ${entry.type === 'DECISION' ? 'bg-blue-500' : entry.type === 'TASK' ? (entry.completed ? 'bg-gray-400' : 'bg-amber-500') : 'bg-gray-400'}`} />
                         <div className="h-full w-px bg-gray-200 mt-1"></div>
                       </div>
                       <div className={`flex-1 p-3 rounded-lg border ${entry.type === 'DECISION' ? 'bg-blue-50 border-blue-100' : entry.type === 'TASK' ? (entry.completed ? 'bg-gray-50 border-gray-100 opacity-75' : 'bg-amber-50 border-amber-100') : 'bg-white border-gray-100'}`}>
                          <div className="flex justify-between items-start">
                            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{entry.type}</span>
                            <span className="text-[10px] text-gray-400">{entry.timestamp}</span>
                          </div>
                          <p className={`text-sm mt-1 ${entry.completed ? 'line-through text-gray-500' : 'text-gray-800'}`}>{entry.content}</p>
                          {entry.audio && (
                            <div className="mt-2 bg-white/50 p-2 rounded border border-gray-200/50">
                               <div className="flex items-center text-[10px] text-gray-500 mb-1">{entry.audio.type === 'RECORDING' ? <Mic className="w-3 h-3 mr-1" /> : <Music className="w-3 h-3 mr-1" />}Audio Note {entry.audio.duration && `• ${entry.audio.duration}`}</div>
                               <audio controls src={entry.audio.url} className="w-full h-6" />
                            </div>
                          )}
                          {entry.type === 'TASK' && (
                            <div className="mt-3 flex items-center space-x-4 text-xs text-gray-600 border-t border-amber-200/50 pt-2">
                              <span className="flex items-center"><Users className="w-3 h-3 mr-1" />{(meeting.participants || []).find(p => p.id === entry.assigneeId)?.name || 'Unassigned'}</span>
                              {entry.dueDate && <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />Due: {entry.dueDate}</span>}
                              <button onClick={() => toggleTaskCompletion(entry.id)} className={`ml-auto px-2 py-1 rounded text-xs ${entry.completed ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>{entry.completed ? 'Completed' : 'Mark Done'}</button>
                            </div>
                          )}
                       </div>
                     </div>
                   ))}
                 </div>
              </div>
            </div>
          )}

          {/* --- NOTES TAB --- */}
          {activeTab === 'notes' && (
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><StickyNote className="w-5 h-5 mr-2 text-indigo-600" />Notes, Ideas & Updates</h3>
                <div className="flex flex-col gap-3 mb-6">
                  <TextArea rows={3} placeholder="Capture an idea, general update, or task status update..." value={noteContent} onChange={(e) => setNoteContent(e.target.value)} className="resize-none" />
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button onClick={() => setNoteCategory('GENERAL')} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center border transition-all ${noteCategory === 'GENERAL' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}><StickyNote className="w-3 h-3 mr-1.5" /> General</button>
                      <button onClick={() => setNoteCategory('IDEA')} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center border transition-all ${noteCategory === 'IDEA' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-yellow-50'}`}><Lightbulb className="w-3 h-3 mr-1.5" /> Idea</button>
                      <button onClick={() => setNoteCategory('UPDATE')} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center border transition-all ${noteCategory === 'UPDATE' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-blue-50'}`}><Info className="w-3 h-3 mr-1.5" /> Update</button>
                      <button onClick={() => setNoteCategory('TASK_UPDATE')} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center border transition-all ${noteCategory === 'TASK_UPDATE' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-green-50'}`}><RefreshCw className="w-3 h-3 mr-1.5" /> Task Update</button>
                    </div>
                    <Button onClick={addNote} disabled={!noteContent.trim()}>Add Note</Button>
                  </div>
                </div>

                {(!meeting.meetingNotes || meeting.meetingNotes.length === 0) ? (
                  <div className="text-center py-10 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200"><StickyNote className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No notes yet. Use this space for freeform ideas and updates.</p></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(meeting.meetingNotes || []).map((note) => (
                      <div key={note.id} className={`p-4 rounded-lg border shadow-sm relative group transition-all hover:shadow-md ${getCategoryColor(note.category)}`}>
                        <div className="flex justify-between items-start mb-2">
                           <div className="flex items-center text-xs font-bold uppercase tracking-wider opacity-70">{getCategoryIcon(note.category)}<span className="ml-1.5">{(note.category || 'GENERAL').replace('_', ' ')}</span></div>
                           <button onClick={() => deleteNote(note.id)} className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{note.content}</p>
                        <div className="mt-3 pt-3 border-t border-black/5 text-[10px] opacity-60 text-right font-medium">{note.timestamp}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* --- REVIEW TAB --- */}
          {activeTab === 'review' && (
            <div className="space-y-6">
              <Card className="p-6 border-purple-100 shadow-md bg-gradient-to-br from-white to-purple-50/30">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                   <h3 className="text-lg font-bold text-gray-800 flex items-center"><FileText className="w-5 h-5 mr-2 text-purple-600" />Meeting Summary</h3>
                   {aiService && <Button onClick={handleGenerateSummary} isLoading={isGeneratingSummary} variant="secondary" className="bg-white hover:bg-purple-50 text-purple-700 border-purple-200"><Wand2 className="w-4 h-4 mr-2 text-purple-500" />{meeting.summary ? 'Regenerate Summary' : 'Generate with AI'}</Button>}
                 </div>
                 {meeting.summary ? (
                   <div className="bg-white p-4 rounded-lg border border-purple-100 shadow-sm">
                      <textarea className="w-full text-gray-700 text-sm leading-relaxed bg-transparent outline-none resize-none" rows={6} value={meeting.summary} onChange={(e) => handleUpdate('summary', e.target.value)} />
                      <p className="text-xs text-right text-purple-400 mt-2 italic">AI-generated • Edits saved automatically</p>
                   </div>
                 ) : (
                   <div className="text-center py-6 text-gray-500 bg-white/50 rounded-lg border border-dashed border-gray-300"><p>No summary available.</p><p className="text-xs mt-1">Click "Generate with AI" to summarize minutes and outcomes.</p></div>
                 )}
              </Card>

              <Card className="p-6">
                 <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><List className="w-5 h-5 mr-2 text-indigo-600" />Agenda Summary & Outcomes</h3>
                 <div className="space-y-4 divide-y divide-gray-100">
                    {(meeting.agenda || []).length === 0 && <p className="text-gray-400 text-sm italic">No agenda items.</p>}
                    {(meeting.agenda || []).map(item => (
                       <div key={item.id} className="pt-4 first:pt-0">
                          <div className="flex items-center justify-between mb-2"><h4 className="font-medium text-gray-800">{item.title}</h4><span className="text-xs text-gray-500">{item.presenter} ({item.durationMinutes}m)</span></div>
                          {item.resources && item.resources.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                               {item.resources.map(res => (<a key={res.id} href={res.url} target="_blank" rel="noreferrer" className="flex items-center text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded hover:bg-indigo-100 border border-indigo-100">{res.type === 'LINK' ? <ExternalLink className="w-3 h-3 mr-1" /> : <FileText className="w-3 h-3 mr-1" />}{res.title}</a>))}
                            </div>
                          )}
                          {(item.notes || item.decisions) ? (
                            <div className="space-y-2 bg-gray-50 p-3 rounded-md text-sm">
                               {item.notes && (<div><span className="font-semibold text-gray-600 text-xs uppercase tracking-wide">Discussion Notes:</span><p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{item.notes}</p></div>)}
                               {item.decisions && (<div className={item.notes ? 'mt-3 pt-3 border-t border-gray-200' : ''}><span className="font-semibold text-blue-600 text-xs uppercase tracking-wide">Decision:</span><p className="text-blue-900 mt-0.5 font-medium whitespace-pre-wrap">{item.decisions}</p></div>)}
                            </div>
                          ) : (<p className="text-xs text-gray-400 italic">No notes or decisions recorded for this item.</p>)}
                       </div>
                    ))}
                 </div>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <Card className="p-5">
                   <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><CheckCircle className="w-5 h-5 mr-2 text-blue-600" />Key Decisions (Global)</h3>
                   <ul className="space-y-3">
                     {(meeting.minutes || []).filter(m => m.type === 'DECISION').length === 0 && <li className="text-gray-400 italic text-sm">No global decisions recorded.</li>}
                     {(meeting.minutes || []).filter(m => m.type === 'DECISION').map(m => (<li key={m.id} className="flex items-start"><div className="mt-1.5 mr-2 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" /><span className="text-gray-700 text-sm">{m.content}</span></li>))}
                   </ul>
                 </Card>

                 <Card className="p-5">
                   <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><CheckSquare className="w-5 h-5 mr-2 text-amber-600" />Action Items</h3>
                   <ul className="space-y-3">
                     {(meeting.minutes || []).filter(m => m.type === 'TASK').length === 0 && <li className="text-gray-400 italic text-sm">No action items recorded.</li>}
                     {(meeting.minutes || []).filter(m => m.type === 'TASK').map(m => (
                       <li key={m.id} className={`p-3 rounded border transition-colors ${m.completed ? 'bg-gray-50 border-gray-200 opacity-75' : 'bg-amber-50 border-amber-100'}`}>
                         <div className="flex items-start gap-3">
                            <div className="pt-0.5"><input type="checkbox" checked={!!m.completed} onChange={() => toggleTaskCompletion(m.id)} className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer" /></div>
                            <div className="flex-1">
                               <div className={`text-sm font-medium ${m.completed ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{m.content}</div>
                               <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                                 <span>Owner: {(meeting.participants || []).find(p => p.id === m.assigneeId)?.name || 'Unassigned'}</span>
                                 {m.dueDate && <span>Due: {m.dueDate}</span>}
                               </div>
                               {m.audio && (
                                  <div className="mt-2 pt-2 border-t border-amber-200/30 flex items-center gap-2">
                                     <div className="text-[10px] text-gray-400 flex items-center">{m.audio.type === 'RECORDING' ? <Mic className="w-3 h-3 mr-1" /> : <Music className="w-3 h-3 mr-1" />}Audio Note</div>
                                     <audio controls src={m.audio.url} className="h-6 w-32" />
                                  </div>
                               )}
                            </div>
                         </div>
                       </li>
                     ))}
                   </ul>
                 </Card>
              </div>

              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Transcript</h3>
                <div className="prose prose-sm max-w-none text-gray-600">
                  {(meeting.minutes || []).length === 0 && <p className="text-gray-400 italic">No minutes recorded.</p>}
                  {(meeting.minutes || []).map(m => (<div key={m.id} className="mb-2"><span className="font-bold text-gray-900">[{m.timestamp}]</span> {m.content}</div>))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiaryMeetingEditor;